import React, { useEffect, useRef, useState } from 'react';
import { toWebpPath, imageDetailUrl } from '../utils/image';
import { fetchLatestImages, ImageMetadata } from '../services/api';
import { fetchWorks, Work, workTitle } from '../services/work';
import { useContent } from '../contexts/ContentContext';
import { getConfig } from '../config';
import { AnimatePresence, motion } from 'framer-motion';

interface RecentMediaSidebarProps {
    showImages?: boolean;
    showWorks?: boolean;
}

const RecentMediaSidebar: React.FC<RecentMediaSidebarProps> = ({
    showImages = true,
    showWorks = true
}) => {
    const { t } = useContent();
    const { apiBaseUrl, apiHost } = getConfig();
    const [recentImages, setRecentImages] = useState<ImageMetadata[]>([]);
    const [recentWorks, setRecentWorks] = useState<Work[]>([]);

    // Rotation state
    const [currentSetIndex, setCurrentSetIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [slideDirection, setSlideDirection] = useState(1);
    const prevSetIndexRef = useRef(0);
    const itemsPerSet = 6;
    const maxItems = 30;

    useEffect(() => {
        const loadRecent = async () => {
            try {
                // Fetch up to 30 images for rotation
                const images = showImages ? await fetchLatestImages(maxItems) : [];
                setRecentImages(images);
            } catch (error) {
                console.error('Failed to load recent images:', error);
            }

            try {
                const works = showWorks ? (await fetchWorks()).sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || '')).slice(0, 6) : [];
                setRecentWorks(works);
            } catch (error) {
                console.error('Failed to load recent works:', error);
            }
        };

        loadRecent();
    }, [showImages, showWorks]);

    // Auto-rotation logic
    useEffect(() => {
        if (isPaused || recentImages.length <= itemsPerSet || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const timer = setInterval(() => {
            setCurrentSetIndex(prev => {
                const next = prev + 1;
                const totalSets = Math.ceil(recentImages.length / itemsPerSet);
                return next >= totalSets ? 0 : next;
            });
        }, 5000); // Rotate every 5 seconds

        return () => clearInterval(timer);
    }, [isPaused, recentImages.length]);

    const visibleImages = recentImages.slice(
        currentSetIndex * itemsPerSet,
        (currentSetIndex + 1) * itemsPerSet
    );

    const totalSets = Math.ceil(recentImages.length / itemsPerSet);
    const hasMultipleSets = totalSets > 1;

    useEffect(() => {
        const prev = prevSetIndexRef.current;
        if (hasMultipleSets) {
            let direction = currentSetIndex > prev ? 1 : -1;
            if (prev === totalSets - 1 && currentSetIndex === 0) {
                direction = 1;
            } else if (prev === 0 && currentSetIndex === totalSets - 1) {
                direction = -1;
            }
            setSlideDirection(direction);
        }
        prevSetIndexRef.current = currentSetIndex;
    }, [currentSetIndex, hasMultipleSets, totalSets]);

    const getImageThumbUrl = (img: ImageMetadata): string => {
        if ((img as any).thumb) {
            const thumbPath = (img as any).thumb as string;
            if (thumbPath.startsWith('http')) return thumbPath;
            if (thumbPath.startsWith('/')) return `${apiHost.replace(/\/+$/, '')}${thumbPath}`;
            const base = apiBaseUrl.replace(/\/+$/, '');
            const path = thumbPath.replace(/^\/+/, '');
            return `${base}/${path}`;
        }
        return '';
    };

    const getImageDetailUrl = imageDetailUrl;

    return (
        <div className="space-y-4">
            {showImages && (
                <div
                    className="rounded-2xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-800 ivory:bg-coffee-100 shadow-sm p-4"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 ivory:text-coffee-700 uppercase tracking-widest">
                            {t('notesRecentMediaSidebar.text1')}
                        </h2>
                        {hasMultipleSets && (
                            <div className="flex gap-1.5">
                                {Array.from({ length: totalSets }).map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentSetIndex(i)}
                                        className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentSetIndex
                                                ? 'bg-blue-500 scale-125'
                                                : 'bg-gray-300 dark:bg-gray-600'
                                            }`}
                                        aria-label={t('common.pageCount', { current: i + 1, total: totalSets })}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="relative overflow-hidden min-h-[400px]">
                        <AnimatePresence mode="wait" initial={false} custom={slideDirection}>
                            <motion.div
                                key={currentSetIndex}
                                custom={slideDirection}
                                initial={{ y: 24 * slideDirection, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -24 * slideDirection, opacity: 0 }}
                                transition={{ duration: 0.4, ease: 'easeInOut' }}
                                className="space-y-3"
                            >
                            {recentImages.length === 0 && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t('notesRecentMediaSidebar.text2')}
                                </p>
                            )}
                            {visibleImages.map(img => (
                                <a
                                    key={img.hash}
                                    href={getImageDetailUrl(img)}
                                    data-scroll-top="smooth"
                                    className="flex gap-3 items-start group animate-fade-in"
                                >
                                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200 flex-shrink-0">
                                        <img
                                            src={getImageThumbUrl(img)}
                                            alt=""
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-400 dark:text-gray-500 ivory:text-coffee-400">
                                            {t('detail.name', { id: img.id })}
                                        </p>
                                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 ivory:text-coffee-700 group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2">
                                            {img.public_title || t('detail.name', { id: img.id })}
                                        </p>
                                    </div>
                                </a>
                            ))}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            )}

            {showWorks && (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-800 ivory:bg-coffee-100 shadow-sm p-4">
                    <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 ivory:text-coffee-700 uppercase tracking-widest mb-4">
                        {t('notesRecentMediaSidebar.text3')}
                    </h2>
                    <div className="space-y-3">
                        {recentWorks.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t('notesRecentMediaSidebar.text4')}
                            </p>
                        )}
                        {recentWorks.map(work => (
                            <a
                                key={work.id}
                                href={`/?page=gallery&view=song&platform=${encodeURIComponent(work.platform || '')}&id=${work.id}`}
                                className="flex gap-3 items-start group"
                            >
                                <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 ivory:bg-coffee-200 flex-shrink-0">
                                    <img
                                        src={toWebpPath(work.thumbnail || '/assets/landing/logo192.png')}
                                        alt={workTitle(work, t('workPage.untitled'))}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                    />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-gray-400 dark:text-gray-500 ivory:text-coffee-400">
                                        {t(`work.kind.${work.kind}`)}
                                    </p>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 ivory:text-coffee-700 group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2">
                                        {workTitle(work, t('workPage.untitled'))}
                                    </p>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecentMediaSidebar;
